import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import HeroSection from './components/HeroSection';
import BusinessValueSection from './components/BusinessValueSection';
import FeaturesSection from './components/FeaturesSection';
import PricingSection from './components/PricingSection';
import PersonaSection from './components/PersonaSection';
import TrustSignalsSection from './components/TrustSignalsSection';
import CTASection from './components/CTASection';
import FooterSection from './components/FooterSection';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userRole, signOut } = useAuth();
  const [showContactModal, setShowContactModal] = useState(false);

  const handleDashboard = () => {
    if (userRole === 'admin') navigate('/admin-dashboard');
    else if (userRole === 'expert') navigate('/expert-dashboard');
    else navigate('/client-dashboard');
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleGetStarted = () => {
    navigate('/new-case-creation-wizard');
  };

  const handleContactSales = () => {
    setShowContactModal(true);
  };

  const handleSelectPlan = (planId) => {
    navigate('/new-case-creation-wizard', { state: { selectedPlan: planId } });
  };

  const handleCloseContactModal = () => {
    setShowContactModal(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center">
              <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
            </div>

            <nav className="hidden lg:flex items-center gap-8">
              <button
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Features
              </button>
              <button
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/expert-marketplace-listing')}
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Experts
              </button>
              <button
                onClick={() => document.getElementById('personas')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Who We Serve
              </button>
            </nav>

            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  <Button
                    variant="default"
                    size="default"
                    onClick={handleDashboard}
                  >
                    Go to Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    size="default"
                    iconName="LogOut"
                    onClick={handleLogout}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="default"
                    onClick={() => navigate('/login')}
                    className="hidden md:inline-flex"
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="default"
                    size="default"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={() => navigate('/signup')}
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      <main>
        <HeroSection onGetStarted={handleGetStarted} onContactSales={handleContactSales} />
        
        <BusinessValueSection />

        <div id="features">
          <FeaturesSection />
        </div>

        <div id="pricing">
          <PricingSection onSelectPlan={handleSelectPlan} onContactSales={handleContactSales} />
        </div>

        <div id="personas">
          <PersonaSection />
        </div>

        <TrustSignalsSection />

        <CTASection onGetStarted={handleGetStarted} onContactSales={handleContactSales} />
      </main>
      <FooterSection />
      {showContactModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={handleCloseContactModal}
          />
          <div className="relative bg-card border border-border rounded-xl shadow-elevation-xl max-w-md w-full p-6 md:p-8">
            <button
              onClick={handleCloseContactModal}
              className="absolute top-4 right-4 w-8 h-8 rounded-lg hover:bg-muted transition-smooth flex items-center justify-center"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} />
            </button>

            <div className="mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="MessageSquare" size={24} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Contact Enterprise
              </h3>
              <p className="text-muted-foreground">
                Tell us what you need and we’ll respond by email with pricing and next steps.
              </p>
            </div>

            <form
              className="space-y-4 mb-6"
              onSubmit={async (e) => {
                e?.preventDefault();
                const fd = new FormData(e.currentTarget);
                const company = fd.get('company');
                const email = fd.get('email');
                const cases = fd.get('cases');
                const message = fd.get('message');

                const submitBtn = e.currentTarget.querySelector('[type="submit"]');
                if (submitBtn) submitBtn.disabled = true;

                try {
                  // No .select() — non-admin users have no SELECT policy on enterprise_leads
                  const { error: insertError } = await supabase
                    ?.from('enterprise_leads')
                    ?.insert({
                      full_name: company,
                      email,
                      company_name: company,
                      estimated_monthly_cases: parseInt(cases) || null, // lower bound of selected range
                      message: message || null,
                      status: 'new',
                      source: 'landing_page_modal',
                      metadata: { monthly_cases_range: cases }
                    });

                  if (insertError) throw insertError;

                  const { data: fnData, error: fnError } = await supabase?.functions?.invoke('send-enterprise-lead-notification', {
                    body: {
                      leadId: null,
                      contactData: {
                        fullName: company,
                        email,
                        companyName: company,
                        estimatedMonthlyCases: cases,
                        message: message || ''
                      }
                    }
                  });

                  if (fnError) {
                    // FunctionsHttpError wraps a Response in .context
                    let detail = fnError.message;
                    try {
                      const body = await fnError.context?.json?.();
                      if (body?.error) detail = body.error;
                    } catch (_) { /* ignore parse errors */ }
                    throw new Error(detail);
                  }

                  // Also check if the function itself returned { error: ... }
                  if (fnData?.error) {
                    throw new Error(fnData.error);
                  }

                  toast.success('Thanks! We\'ll be in touch shortly.');
                  setShowContactModal(false);
                } catch (err) {
                  console.error('Contact sales submit error:', err);
                  toast.error(err?.message || 'Failed to submit. Please email us directly.');
                  if (submitBtn) submitBtn.disabled = false;
                }
              }}
            >
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="company">
                  Company
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  required
                  className="w-full h-11 px-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Your organization"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="email">
                  Work Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full h-11 px-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="name@company.com"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="cases">
                  Expected monthly cases
                </label>
                <select
                  id="cases"
                  name="cases"
                  required
                  className="w-full h-11 px-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select range
                  </option>
                  <option value="11-25">11–25</option>
                  <option value="26-50">26–50</option>
                  <option value="51-100">51–100</option>
                  <option value="100+">100+</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="message">
                  Notes
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="What outcomes do you need? Any special requirements?"
                />
                <p className="text-xs text-muted-foreground">
                  We’ll follow up by email. No phone support required.
                </p>
              </div>

              <Button variant="default" fullWidth iconName="Send" iconPosition="right" type="submit">
                Submit Request
              </Button>
            </form>

            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <Icon name="Mail" size={20} className="text-primary" />
              <div>
                <div className="text-sm font-medium text-foreground">Or email us</div>
                <div className="text-sm text-muted-foreground">enterprise@fireforensics.com</div>
              </div>
            </div>

            <Button
              variant="ghost"
              fullWidth
              className="mt-4"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => (window.location.href = 'mailto:enterprise@fireforensics.com')}
            >
              Open Email Client
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
